import {
  FieldWrapper,
  InputSelectionList,
  InputSelectionListProps,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';

import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'utils/hookForm';

type InputSelectionListPropsWithoutChildren<TItemValue> = Omit<
  InputSelectionListProps<TItemValue>,
  'children' | 'multiple'
>;

/**
 * Represents the React Hook Form variant of the {@link InputSelectionList}
 * component. With a Single value.
 */
export const HookFormSelectionListSingle = <
  TItemValue extends FieldPathValue<TFieldValues, TName>,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  InputSelectionListPropsWithoutChildren<TItemValue>
> &
  PropsWithChildren): JSX.Element => {
  const { controller, controllerProps, fieldWrapperProps } =
    useHookFormFieldWrapper<
      TFieldValues,
      TName,
      InputSelectionListPropsWithoutChildren<TItemValue>
    >(props);
  const {
    name,
    onBlur: onBlurController,
    onChange: onChangeController,
    ...controllerFieldProps
  } = controller.field;

  return (
    <FieldWrapper {...fieldWrapperProps}>
      <InputSelectionList
        {...props.componentProps}
        {...controllerFieldProps}
        multiple={false}
        value={controllerFieldProps.value ? [controllerFieldProps.value] : []}
        onBlur={(event) => {
          props.componentProps?.onBlur?.(event);
          onBlurController();
        }}
        onChange={(event) => {
          props.componentProps?.onChange?.(event);
          onChangeController(event.selectedValues[0] ?? null);
        }}
        id={controllerProps.name}
        aria-disabled={fieldWrapperProps.disabled}
        disabled={fieldWrapperProps.disabled}
        aria-required={fieldWrapperProps.required}
        required={fieldWrapperProps.required}
        status={FieldWrapper.getStatus(fieldWrapperProps.meta)}
      >
        {children}
      </InputSelectionList>
    </FieldWrapper>
  );
};
